import { ChangeEvent, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AddressFields } from "@/components/forms/AddressFields";
import { formatPhoneInputValue } from "@/lib/phone";
import type { AddressLike } from "@/lib/address";
import { composeAddressLine } from "@/lib/address";
import type { InsertEmergencyContact } from "@shared/schema";

const RELATIONSHIP_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Spouse", value: "Spouse" },
  { label: "Parent", value: "Parent" },
  { label: "Child", value: "Child" },
  { label: "Sibling", value: "Sibling" },
  { label: "Grandparent", value: "Grandparent" },
  { label: "Grandchild", value: "Grandchild" },
  { label: "Aunt/Uncle", value: "Aunt/Uncle" },
  { label: "Cousin", value: "Cousin" },
  { label: "Friend", value: "Friend" },
  { label: "Partner", value: "Partner" },
  { label: "Guardian", value: "Guardian" },
  { label: "Other", value: "Other" },
];

const CONTACT_TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Primary", value: "Primary" },
  { label: "Secondary", value: "Secondary" },
  { label: "Terciary", value: "Terciary" },
];

const EMERGENCY_ADDRESS_FIELD_OVERRIDES = {
  address1: "emergencyAddress1",
  address2: "emergencyAddress2",
  city: "emergencyCity",
  state: "emergencyState",
  zipCode: "emergencyZipCode",
  county: "emergencyCounty",
  country: "emergencyCountry",
} as const;

type EmergencyContactSectionProps = {
  form: UseFormReturn<any>;
};

const PHONE_PLACEHOLDER = "(555) 987-6543";

export const defaultEmergencyContactValues = {
  emergencyRelationship: "",
  emergencyContactType: "Primary",
  emergencySameAddress: true,
  emergencyAddress1: "",
  emergencyAddress2: "",
  emergencyCity: "",
  emergencyState: "",
  emergencyZipCode: "",
  emergencyCounty: "",
  emergencyCountry: "United States",
};

export type EmergencyContactFormValues = {
  emergencyContact?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
  emergencyContactType?: string;
  emergencySameAddress?: boolean;
  emergencyAddress1?: string;
  emergencyAddress2?: string;
  emergencyCity?: string;
  emergencyState?: string;
  emergencyZipCode?: string;
  emergencyCounty?: string;
  emergencyCountry?: string;
};

export const EMERGENCY_RELATIONSHIP_OPTIONS = RELATIONSHIP_OPTIONS;
export const EMERGENCY_CONTACT_TYPE_OPTIONS = CONTACT_TYPE_OPTIONS;

export function EmergencyContactSection({ form }: EmergencyContactSectionProps) {
  const { control, watch, setValue } = form;

  const emergencySameAddress = Boolean(watch("emergencySameAddress"));

  const emergencyAddressFieldNames = useMemo(() => {
    return {
      address1: EMERGENCY_ADDRESS_FIELD_OVERRIDES.address1,
      address2: EMERGENCY_ADDRESS_FIELD_OVERRIDES.address2,
      city: EMERGENCY_ADDRESS_FIELD_OVERRIDES.city,
      state: EMERGENCY_ADDRESS_FIELD_OVERRIDES.state,
      zipCode: EMERGENCY_ADDRESS_FIELD_OVERRIDES.zipCode,
      county: EMERGENCY_ADDRESS_FIELD_OVERRIDES.county,
      country: EMERGENCY_ADDRESS_FIELD_OVERRIDES.country,
    };
  }, []);

  const handlePhoneChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const formattedValue = formatPhoneInputValue(event.target.value);
    onChange(formattedValue);
  };

  const handleSameAddressChange = (checked: boolean) => {
    setValue("emergencySameAddress", checked, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const renderContactNameField = ({ field }: any) => {
    return (
      <FormItem className="col-span-12">
        <FormLabel>Emergency Contact <span className="text-red-500">*</span></FormLabel>
        <FormControl>
          <Input 
            placeholder="Full Name" 
            {...field}
            value={field.value ?? ""}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    );
  };

  const renderContactPhoneField = ({ field }: any) => {
    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      handlePhoneChange(event, field.onChange);
    };

    return (
      <FormItem className="col-span-12 md:col-span-6">
        <FormLabel>Phone <span className="text-red-500">*</span></FormLabel>
        <FormControl>
          <Input
            {...field}
            value={field.value || ""}
            placeholder={PHONE_PLACEHOLDER}
            onChange={onInputChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    );
  };

  const renderRelationshipField = ({ field }: any) => {
    return (
      <FormItem className="col-span-12 md:col-span-6">
        <FormLabel>Relationship <span className="text-red-500">*</span></FormLabel>
        <Select value={field.value || ""} onValueChange={field.onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select relationship" />
          </SelectTrigger>
          <SelectContent>
            {RELATIONSHIP_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    );
  };

  const renderContactTypeField = ({ field }: any) => {
    return (
      <FormItem className="col-span-12 md:col-span-6">
        <FormLabel>Priority</FormLabel>
        <Select value={field.value || ""} onValueChange={field.onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {CONTACT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    );
  };

  const renderEmailField = ({ field }: any) => {
    return (
      <FormItem className="col-span-12 md:col-span-6">
        <FormLabel>Email</FormLabel>
        <FormControl>
          <Input 
            type="email" 
            placeholder="name@example.com" 
            {...field}
            value={field.value ?? ""}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    );
  };

  const renderSameAddressField = ({ field }: any) => {
    const onCheckedChange = (checked: boolean) => {
      handleSameAddressChange(checked);
    };

    return (
      <FormItem className="col-span-12">
        <div className="flex items-center space-x-2">
          <FormControl>
            <Checkbox
              checked={Boolean(field.value)}
              onCheckedChange={(value) =>
                onCheckedChange(Boolean(value) as boolean)
              }
            />
          </FormControl>
          <div>
            <FormLabel className="text-sm font-medium">
              Same address as employee
            </FormLabel>
            <FormDescription className="text-xs text-muted-foreground">
              Uncheck to enter a different address for the emergency contact.
            </FormDescription>
          </div>
        </div>
        <FormMessage />
      </FormItem>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={control}
          name={"emergencyContact" as any}
          render={renderContactNameField}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={control}
          name={"emergencyRelationship" as any}
          render={renderRelationshipField}
        />
        <FormField
          control={control}
          name={"emergencyContactType" as any}
          render={renderContactTypeField}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <FormField
          control={control}
          name={"emergencyPhone" as any}
          render={renderContactPhoneField}
        />
        <FormField
          control={control}
          name={"email" as any}
          render={renderEmailField}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-muted-foreground">Address</p>
        <div className="grid grid-cols-12 gap-4">
          <FormField
            control={control}
            name={"emergencySameAddress" as any}
            render={renderSameAddressField}
          />
        </div>

        {!emergencySameAddress && (
          <div className="rounded-md border p-4">
            <AddressFields
              control={control}
              fieldNames={emergencyAddressFieldNames as any}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function deriveEmergencyContactAddress(
  values: EmergencyContactFormValues,
  employeeAddress: AddressLike
): AddressLike {
  if (values.emergencySameAddress) {
    return employeeAddress;
  }

  return {
    address1: values.emergencyAddress1 ?? "",
    address2: values.emergencyAddress2 ?? "",
    city: values.emergencyCity ?? "",
    state: values.emergencyState ?? "",
    zipCode: values.emergencyZipCode ?? "",
    county: values.emergencyCounty ?? "",
    country: values.emergencyCountry ?? "United States",
  };
}

export type EmergencyContactPayload = Omit<
  InsertEmergencyContact,
  "employeeId"
>;

export function buildEmergencyContactPayload(
  values: EmergencyContactFormValues,
  employeeAddress: AddressLike
): EmergencyContactPayload | null {
  const contactName = (values.emergencyContact ?? "").trim();
  const contactPhone = (values.emergencyPhone ?? "").trim();

  if (contactName.length === 0 || contactPhone.length === 0) {
    return null;
  }

  const relationship = (values.emergencyRelationship ?? "").trim();
  const contactType = (values.emergencyContactType ?? "Primary").trim() || "Primary";
  const addressValues = deriveEmergencyContactAddress(values, employeeAddress);
  const formattedAddress = composeAddressLine(addressValues);

  return {
    name: contactName,
    phone: contactPhone,
    relationship: relationship.length > 0 ? relationship : "Other",
    contactType: contactType,
    isPrimary: contactType === "Primary",
    address: formattedAddress,
    address1: addressValues.address1 ?? "",
    address2: addressValues.address2 ?? "",
    city: addressValues.city ?? "",
    state: addressValues.state ?? "",
    zipCode: addressValues.zipCode ?? "",
    county: addressValues.county ?? "",
    country: addressValues.country ?? "United States",
  };
}

export type SegregatedEmergencyContactValues = {
  employeeFields: Record<string, any>;
  emergencyValues: EmergencyContactFormValues;
  employeeAddress: AddressLike;
};

export function separateEmergencyContactValues(
  values: Record<string, any>
): SegregatedEmergencyContactValues {
  const {
    emergencyRelationship,
    emergencyContactType,
    emergencySameAddress,
    emergencyAddress1,
    emergencyAddress2,
    emergencyCity,
    emergencyState,
    emergencyZipCode,
    emergencyCounty,
    emergencyCountry,
    ...employeeFields
  } = values;

  const employeeAddress: AddressLike = {
    address1: employeeFields.address1 ?? "",
    address2: employeeFields.address2 ?? "",
    city: employeeFields.city ?? "",
    state: employeeFields.state ?? "",
    zipCode: employeeFields.zipCode ?? "",
    county: employeeFields.county ?? "",
    country: employeeFields.country ?? "United States",
  };

  const emergencyValues: EmergencyContactFormValues = {
    emergencyContact: employeeFields.emergencyContact,
    emergencyPhone: employeeFields.emergencyPhone,
    emergencyRelationship,
    emergencyContactType,
    emergencySameAddress,
    emergencyAddress1,
    emergencyAddress2,
    emergencyCity,
    emergencyState,
    emergencyZipCode,
    emergencyCounty,
    emergencyCountry,
  };

  return {
    employeeFields,
    emergencyValues,
    employeeAddress,
  };
}

